import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import { useAuth } from '../auth/AuthContext'
import { api } from '../lib/api'
import { AvailabilityLabel } from '../lib/labels'
import ListContainer from '../ui/ListContainer'
import { useToast } from '../ui/ToastProvider'
import PeopleTabs from '../components/PeopleTabs'
import { downloadCsv, parseCsv } from '../utils/csv'
import { Mail, Phone } from 'lucide-react'

// 원수사 디렉터리 페이지
// - 담당자 검색/정렬/즐겨찾기/메모/의뢰 시작 등 상호작용 제공
// - 상태 라벨은 AvailabilityLabel을 사용해 일관된 한국어 표기
export default function CounterpartDirectoryPage() {
    const { show } = useToast()
    const navigate = useNavigate()
    const { user } = useAuth() || { user: null }
    const [importPreviewOpen, setImportPreviewOpen] = useState(false)
    const [importPreviewLoading, setImportPreviewLoading] = useState(false)
    const [importApplying, setImportApplying] = useState(false)
    const [importPayload, setImportPayload] = useState(null)
    const [importPreview, setImportPreview] = useState(null)
    const [csvHelpOpen, setCsvHelpOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [savingAssignment, setSavingAssignment] = useState(new Set())
    const [savingMode, setSavingMode] = useState(false)
    const [createInsurerUserOpen, setCreateInsurerUserOpen] = useState(false)
    const [createInsurerUserForm, setCreateInsurerUserForm] = useState({
        name: '',
        email: '', // (선택) 직접 지정 시 /users 사용, 비우면 더미 이메일 자동 생성
        phoneNumber: '',
        role: 'INSURER_OPERATION_MANAGER',
        password: '',
        gaBranchOrgId: '', // (GA_PROXY 생성 시) 배정할 GA 지점/지사
    })
    const [gaBranchOptions, setGaBranchOptions] = useState([]) // [{id,name,type,parentOrgId}]

    // 검색 및 필터 상태
    const [searchQuery, setSearchQuery] = useState('')
    const [statusFilter, setStatusFilter] = useState('ALL')
    const [orgFilter, setOrgFilter] = useState('ALL') // 원수사 유저용 GA 필터
    // OPERATOR 전용: 조회 모드 및 GA 조직 관점(조직별 보기)
    // 기본은 GA 조직별로 두어(요구사항) 진입 시 대량 로딩을 방지한다.
    const [operatorViewMode, setOperatorViewMode] = useState('BY_GA_ORG') // ALL | BY_GA_ORG | BY_INSURER
    const [operatorViewOrgId, setOperatorViewOrgId] = useState('') // BY_GA_ORG일 때만 사용
    const [operatorGaL1, setOperatorGaL1] = useState('')
    const [operatorGaL2, setOperatorGaL2] = useState('')
    const [operatorGaL3, setOperatorGaL3] = useState('')
    const [operatorInsurerOrgId, setOperatorInsurerOrgId] = useState('') // BY_INSURER일 때만 사용(보험사 회사 노드 권장)
    const [onlyGaManagerCreated, setOnlyGaManagerCreated] = useState(false) // 운영자: GA관리자 생성 필터
    const [kpiFilter] = useState('ALL')
    const [sortBy, setSortBy] = useState('responseTime')

    // 데이터 상태
    const [counterparts, setCounterparts] = useState([])
    const [orgOptions, setOrgOptions] = useState([]) // GA: 매핑된 원수사, 원수사: 매핑된 GA
    const [favorites, setFavorites] = useState(new Set())
    const [userNotes, setUserNotes] = useState({})
    const [noteDraft, setNoteDraft] = useState('')

    // 선택된 담당자 상세 정보
    const [selectedCounterpart, setSelectedCounterpart] = useState(null)
    const [showDetailModal, setShowDetailModal] = useState(false)
    const [detailEditMode, setDetailEditMode] = useState(false)
    const [detailSaving, setDetailSaving] = useState(false)
    const [detailEditForm, setDetailEditForm] = useState({ name: '', phoneNumber: '', gaBranchOrgId: '' })
    const [selectedGaBranches, setSelectedGaBranches] = useState([]) // [{gaBranchOrgId, gaBranchOrgName, ...}]
    const [loadingSelectedGaBranches, setLoadingSelectedGaBranches] = useState(false)

    // 원수사(회사)별 "담당자 배정 여부" 캐시
    const [assignedByCompanyId, setAssignedByCompanyId] = useState({}) // { [companyOrgId: string]: boolean }
    const [checkingAssignedCompanyIds, setCheckingAssignedCompanyIds] = useState(new Set()) // Set<string>

    const debouncedSearch = useDebouncedValue(searchQuery, 300)

    const roles = Array.isArray(user?.roles) ? user.roles : []
    const isGaUser = roles.some(r => r.startsWith('GA_'))
    const isInsurerUser = roles.some(r => r.startsWith('INSURER_'))
    const isPersonalUser = String(user?.orgType || '').toUpperCase() === 'PERSONAL'
    // 원수사 담당자(배정/해제·대리 생성·CSV·모드 전환): GA 조직 관리자만. 설계사(GA_PLANNER)·GA 매니저 등은 조회만.
    const canManageGaInsurerCounterparts = roles.includes('OPERATOR')
        || roles.includes('GA_LEADER')
        || roles.includes('GA_MANAGER')
        || roles.includes('GA_MASTER_ADMIN')
        || roles.includes('GA_MASTER_DEPUTY_ADMIN')
        || roles.includes('GA_DEPUTY_LEADER')
        || roles.includes('GA_BRANCH_MANAGER')
        || roles.includes('GA_DEPUTY_BRANCH_MANAGER')
        || roles.includes('GA_OTHER_ADMIN')
    // 정책:
    // - GA(설계사 포함)는 "원수사 담당자" 상세 팝업을 볼 수 있다(수정은 불가).
    // - INSURER 사용자는 "GA 사용자(우리 조직 사용자)" 상세는 노출하지 않는다. (필요 시 OPERATOR만 예외)
    const isOperator = roles.includes('OPERATOR')
    const canViewCounterpartDetail = isGaUser || isOperator

    const canEditGaProxyCounterpart = useMemo(() => {
        // 운영자는 GA 관리자가 생성한 원수사 담당자도 수정 가능
        if (isOperator) {
            if (!selectedCounterpart) return false
            return selectedCounterpart.createdSource === 'GA_PROXY'
        }
        // GA 사용자는 기존 로직 유지
        if (!isGaUser) return false
        if (!canManageGaInsurerCounterparts) return false
        if (!selectedCounterpart) return false
        return selectedCounterpart.createdSource === 'GA_PROXY'
    }, [isOperator, isGaUser, canManageGaInsurerCounterparts, selectedCounterpart])

    const reloadSelectedGaBranches = useCallback(async () => {
        if (!selectedCounterpart?.id) return
        try {
            const res = await api.get(`/users/${selectedCounterpart.id}/ga-branches`)
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const list = Array.isArray(payload) ? payload : []
            setSelectedGaBranches(list)
        } catch {
            setSelectedGaBranches([])
        }
    }, [selectedCounterpart])

    // CSV 도움말 팝오버: 바깥 클릭 시 닫기
    useEffect(() => {
        if (!csvHelpOpen) return
        const onDown = (e) => {
            // 팝오버 내부 클릭은 무시 (data-csv-help)
            const el = e?.target
            if (el && el.closest && el.closest('[data-csv-help]')) return
            setCsvHelpOpen(false)
        }
        document.addEventListener('mousedown', onDown)
        return () => document.removeEventListener('mousedown', onDown)
    }, [csvHelpOpen])

    const addGaBranchMappingForSelected = useCallback(async () => {
        if (!selectedCounterpart?.id) return
        const desired = detailEditForm.gaBranchOrgId ? Number(detailEditForm.gaBranchOrgId) : null
        if (!desired) return show('추가할 담당 지사를 선택해주세요', 'error')
        setDetailSaving(true)
        try {
            await api.post(`/users/${selectedCounterpart.id}/ga-branches`, { gaBranchOrgId: desired })
            await reloadSelectedGaBranches()
            // reload는 파일 아래쪽에서 선언되는 useCallback이라 TDZ 문제가 날 수 있어,
            // 직접 호출 대신 동일한 요청을 수행하는 effect에 맡긴다.
            show('담당 지사가 추가되었습니다', 'success')
        } catch (err) {
            const msg =
                err?.response?.data?.message
                || err?.response?.data?.msg
                || '담당 지사 추가에 실패했습니다. (권한/조직 범위를 확인해주세요)'
            show(msg, 'error')
        } finally {
            setDetailSaving(false)
        }
    }, [detailEditForm.gaBranchOrgId, reloadSelectedGaBranches, selectedCounterpart, show])

    const removeGaBranchMappingForSelected = useCallback(async (gaBranchOrgId, gaBranchOrgName) => {
        if (!selectedCounterpart?.id) return
        const label = (gaBranchOrgName || '').toString().trim() || '해당 조직/지사'
        const ok = window.confirm(`${label}에서 생성 및 배정한 매니저입니다. 삭제하시겠습니까?`)
        if (!ok) return
        setDetailSaving(true)
        try {
            const res = await api.delete(`/users/${selectedCounterpart.id}/ga-branches/${Number(gaBranchOrgId)}`)
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            if (payload && payload.deleted === false) {
                show('이미 배정 해제된 상태입니다', 'success')
            } else {
                show('배정이 해제되었습니다', 'success')
            }
            await reloadSelectedGaBranches()
        } catch (err) {
            const msg =
                err?.response?.data?.message
                || err?.response?.data?.msg
                || '배정 해제에 실패했습니다.'
            show(msg, 'error')
        } finally {
            setDetailSaving(false)
        }
    }, [reloadSelectedGaBranches, selectedCounterpart, show])

    // 상세 모달 오픈 시: GA_PROXY 담당 지사 매핑 로딩
    useEffect(() => {
        let ignore = false
        if (!showDetailModal || !selectedCounterpart?.id) return () => { ignore = true }
        if (!canEditGaProxyCounterpart) return () => { ignore = true }
        ; (async () => {
            if (!ignore) setLoadingSelectedGaBranches(true)
            try {
                const res = await api.get(`/users/${selectedCounterpart.id}/ga-branches`)
                const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
                const list = Array.isArray(payload) ? payload : []
                if (!ignore) {
                    setSelectedGaBranches(list)
                    // 편집 중에 서버값이 덮어쓰지 않도록, 기본값 세팅은 편집 모드가 아닐 때만
                    if (!detailEditMode) {
                        const primary = list[0]
                        if (primary?.gaBranchOrgId != null) {
                            setDetailEditForm(prev => ({ ...prev, gaBranchOrgId: String(primary.gaBranchOrgId) }))
                        }
                    }
                }
            } catch {
                if (!ignore) setSelectedGaBranches([])
            } finally {
                if (!ignore) setLoadingSelectedGaBranches(false)
            }
        })()
        return () => { ignore = true }
    }, [showDetailModal, selectedCounterpart, canEditGaProxyCounterpart, detailEditMode])

    // GA: 보험 유형(생명/손해) 및 선택된 원수사
    const [insurerCategory, setInsurerCategory] = useState('NONLIFE') // ALL | LIFE | NONLIFE, 기본 손해보험
    const [selectedInsurerId, setSelectedInsurerId] = useState(null)
    const [gaViewMode, setGaViewMode] = useState('CARDS') // CARDS | ALL_LIST
    const [allListPage, setAllListPage] = useState(1)
    const [allListPageSize, setAllListPageSize] = useState(50)

    // 전체 원수사 보기: 필터/정렬/모드가 바뀌면 1페이지로
    useEffect(() => {
        if (!isGaUser) return
        if (gaViewMode !== 'ALL_LIST') return
        setAllListPage(1)
    }, [isGaUser, gaViewMode, debouncedSearch, statusFilter, sortBy])

    const ensureAssignedForCompany = useCallback(async (companyOrgId, options = {}) => {
        const id = String(companyOrgId)
        const force = Boolean(options?.force)
        if (!force && Object.prototype.hasOwnProperty.call(assignedByCompanyId, id)) {
            return !!assignedByCompanyId[id]
        }
        if (checkingAssignedCompanyIds.has(id)) {
            return null
        }
        setCheckingAssignedCompanyIds(prev => new Set([...prev, id]))
        try {
            const res = await api.get('/counterparts/assignment-status', { params: { orgIds: id } })
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const list = Array.isArray(payload) ? payload : []
            const row = list.find(r => String(r?.orgId) === id) || null
            const ok = !!row?.assigned
            setAssignedByCompanyId(prev => ({ ...prev, [id]: ok }))
            return ok
        } catch {
            setAssignedByCompanyId(prev => ({ ...prev, [id]: false }))
            return false
        } finally {
            setCheckingAssignedCompanyIds(prev => {
                const next = new Set(prev)
                next.delete(id)
                return next
            })
        }
    }, [assignedByCompanyId, checkingAssignedCompanyIds])

    const ensureAssignedBatch = useCallback(async (companyOrgIds) => {
        const ids = Array.isArray(companyOrgIds) ? companyOrgIds.map(x => String(x)).filter(Boolean) : []
        if (ids.length === 0) return
        const missing = ids.filter(id => !Object.prototype.hasOwnProperty.call(assignedByCompanyId, id))
        if (missing.length === 0) return
        try {
            const res = await api.get('/counterparts/assignment-status', { params: { orgIds: missing.join(',') } })
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const list = Array.isArray(payload) ? payload : []
            const next = {}
            list.forEach(it => {
                const key = it?.orgId != null ? String(it.orgId) : null
                if (!key) return
                next[key] = !!it.assigned
            })
            if (Object.keys(next).length > 0) {
                setAssignedByCompanyId(prev => ({ ...prev, ...next }))
            }
        } catch {
            // ignore
        }
    }, [assignedByCompanyId])

    const reload = useCallback(async () => {
        // 운영자:
        // - 전체 보기(ALL): viewAll=true로 조회 가능
        // - 조직별(BY_GA_ORG): 먼저 GA 조직 관점을 선택해야 조회가 가능
        if (isOperator && operatorViewMode === 'BY_GA_ORG' && !operatorViewOrgId) {
            setCounterparts([])
            setFavorites(new Set())
            setUserNotes({})
            setLoading(false)
            setError(null)
            return
        }
        // - 원수사별(BY_INSURER): 먼저 원수사를 선택해야 조회가 가능
        if (isOperator && operatorViewMode === 'BY_INSURER' && !operatorInsurerOrgId) {
            setCounterparts([])
            setFavorites(new Set())
            setUserNotes({})
            setLoading(false)
            setError(null)
            return
        }
        setLoading(true)
        setError(null)
        try {
            const params = {
                q: debouncedSearch || undefined,
                status: statusFilter !== 'ALL' ? statusFilter : undefined,
                kpiRange: kpiFilter !== 'ALL' ? kpiFilter : undefined,
                sortBy
            }

            // GA: 카드 뷰에서는 선택된 원수사만, 전체 목록 뷰에서는 전체 원수사(=orgId 필터 없음)
            if (isGaUser && selectedInsurerId && gaViewMode !== 'ALL_LIST') {
                params.orgId = selectedInsurerId
            } else if (isInsurerUser && orgFilter !== 'ALL') {
                params.orgId = orgFilter
            }
            // OPERATOR: 조직 선택 값으로 "조회 관점"을 지정한다.
            if (isOperator) {
                if (operatorViewMode === 'ALL') {
                    params.viewAll = true
                } else if (operatorViewOrgId) {
                    params.viewOrgId = Number(operatorViewOrgId)
                }
                if (operatorViewMode === 'BY_INSURER' && operatorInsurerOrgId) {
                    params.viewAll = true // 운영자 전체 풀 기준이지만, BE에서 orgId로 범위를 먼저 좁힘
                    params.orgId = Number(operatorInsurerOrgId)
                }
                if (onlyGaManagerCreated) {
                    params.createdByGaManager = true
                }
            }

            const [counterpartsRes, favoritesRes, notesRes] = await Promise.all([
                api.get('/counterparts', { params }),
                api.get('/counterparts/favorites'),
                api.get('/counterparts/notes')
            ])

            const cps = Array.isArray(counterpartsRes.data?.data) ? counterpartsRes.data.data : (counterpartsRes.data || [])
            const favs = (favoritesRes.data?.data ?? favoritesRes.data) || []
            const notes = (notesRes.data?.data ?? notesRes.data) || {}

            setCounterparts(cps)
            setFavorites(new Set(favs))
            setUserNotes(notes)
        } catch (err) {
            setError('담당자 정보를 불러오지 못했습니다')
        } finally {
            setLoading(false)
        }
    }, [debouncedSearch, statusFilter, orgFilter, kpiFilter, sortBy, isGaUser, isInsurerUser, selectedInsurerId, gaViewMode, isOperator, operatorViewMode, operatorViewOrgId, operatorInsurerOrgId, onlyGaManagerCreated])

    // 데이터 로딩
    useEffect(() => {
        let ignore = false
            ; (async () => {
                if (ignore) return
                await reload()
            })()
        return () => { ignore = true }
    }, [reload])

    // NOTE: 탭 배지(배정/미배정 카운트)는 제거하고, 매핑 여부만 표시한다.

    // 조직 선택 옵션: 로그인한 지점에 매핑된 원수사/GA 조직
    useEffect(() => {
        let ignore = false
        async function loadOrgOptions() {
            try {
                if (!user?.orgId) {
                    if (!ignore) setOrgOptions([])
                    return
                }
                const res = await api.get(`/orgs/${user.orgId}`)
                const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data

                let options = []
                if (isGaUser) {
                    options = Array.isArray(payload?.mappedInsurers) ? payload.mappedInsurers : []
                } else if (isInsurerUser) {
                    options = Array.isArray(payload?.mappedGAs) ? payload.mappedGAs : []
                } else {
                    options = []
                }

                if (!ignore) setOrgOptions(options)
            } catch {
                if (!ignore) setOrgOptions([])
            }
        }
        loadOrgOptions()
        return () => { ignore = true }
    }, [user, isGaUser, isInsurerUser])

    // NOTE: selectedInsurerId는 "보험사 회사 노드" id이며, 실제 매핑은 회사 하위의 특정 조직(지점/운영단위)로 존재할 수 있다.

    const generatePassword = React.useCallback(() => {
        // 12자리 정도의 간단한 임시 비밀번호(공유용)
        const s = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)
        return s.slice(0, 12)
    }, [])

    useEffect(() => {
        if (!createInsurerUserOpen) return
        setCreateInsurerUserForm(prev => ({ ...prev, password: prev.password || generatePassword() }))
    }, [createInsurerUserOpen, generatePassword])

    // "배정할 GA 지점/지사" 옵션 로딩 (생성 모달/상세 수정 모달 공용). 개인회원은 본인 조직 1개만.
    // 운영자는 모든 GA 조직을 볼 수 있어야 함
    useEffect(() => {
        let ignore = false
        // 운영자는 모든 GA 조직 조회
        if (isOperator) {
            ; (async () => {
                try {
                    const res = await api.get('/orgs/options', { params: { scope: 'all', type: 'GA' } })
                    const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
                    const list = Array.isArray(payload) ? payload : []
                    const gaBranches = list
                        .filter(o => o && String(o.type).toUpperCase() === 'GA')
                        .filter(o => o.parentOrgId != null) // GA 루트 제외
                    if (!ignore) setGaBranchOptions(gaBranches)
                } catch {
                    if (!ignore) setGaBranchOptions([])
                }
            })()
            return () => { ignore = true }
        }
        // GA 사용자: 기존 로직 유지
        if (!isGaUser) return () => { ignore = true }
        if (!user?.orgId) return () => { ignore = true }
        if (isPersonalUser) {
            if (!ignore) setGaBranchOptions([{ id: user.orgId, name: user.orgName || '내 조직', type: 'PERSONAL', parentOrgId: null }])
            return () => { ignore = true }
        }
        ; (async () => {
            try {
                const res = await api.get('/orgs/options', { params: { scope: 'mine' } })
                const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
                const list = Array.isArray(payload) ? payload : []
                const gaBranches = list
                    .filter(o => o && String(o.type).toUpperCase() === 'GA')
                    .filter(o => o.parentOrgId != null) // GA 루트 제외
                if (!ignore) setGaBranchOptions(gaBranches)
            } catch {
                if (!ignore) setGaBranchOptions([])
            }
        })()
        return () => { ignore = true }
    }, [isOperator, isGaUser, isPersonalUser, user?.orgId, user?.orgName])

    // 생성 모달이 열릴 때만: 기본 배정 지사 설정
    useEffect(() => {
        if (!createInsurerUserOpen) return
        if (!isGaUser) return
        if (!user?.orgId) return
        setCreateInsurerUserForm(prev => ({
            ...prev,
            gaBranchOrgId: prev.gaBranchOrgId || String(user.orgId),
        }))
    }, [createInsurerUserOpen, isGaUser, user])

    const refreshOrgOptions = useCallback(async () => {
        try {
            if (!user?.orgId) return
            const res = await api.get(`/orgs/${user.orgId}`)
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            if (isGaUser) {
                setOrgOptions(Array.isArray(payload?.mappedInsurers) ? payload.mappedInsurers : [])
            } else if (isInsurerUser) {
                setOrgOptions(Array.isArray(payload?.mappedGAs) ? payload.mappedGAs : [])
            }
        } catch {
            // ignore
        }
    }, [user, isGaUser, isInsurerUser])

    const updateManagerAssignmentMode = async (mode, insurerOrgIdForMapping) => {
        if (!isGaUser || !user?.orgId || !insurerOrgIdForMapping) return
        if (!canManageGaInsurerCounterparts) return show('권한이 없습니다', 'error')
        try {
            setSavingMode(true)
            await api.patch(`/orgs/${user.orgId}/mappings/manager-assignment-mode`, { counterpartOrgId: Number(insurerOrgIdForMapping), mode })
            await refreshOrgOptions()
            await reload()
            show('설정이 저장되었습니다', 'success')
        } catch (err) {
            show('설정 저장 실패', 'error')
        } finally {
            setSavingMode(false)
        }
    }

    const toggleGaAssignment = async (counterpart) => {
        if (!isGaUser || !user?.orgId) return
        if (!counterpart?.assignmentEditable) return show('권한이 없습니다', 'error')
        const key = counterpart.id
        const affectedCompanyId = selectedInsurerId || (counterpart.orgId ? resolveCompanyId(counterpart.orgId) : null)
        setSavingAssignment(prev => new Set([...prev, key]))
        try {
            if (counterpart.gaAssigned) {
                // 상위 조직(사업단)에서 "배정해제"를 누르는 경우,
                // 실제 매핑은 하위 지사에 걸려 있어 (userId, 사업단Id) 조합의 매핑 row가 없을 수 있다.
                // 이 경우 "어느 조직/지사에서 생성·배정된 담당자인지"를 확인 문구로 안내한다.
                try {
                    const res = await api.get(`/users/${counterpart.id}/ga-branches`)
                    const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
                    const list = Array.isArray(payload) ? payload : []
                    const hasExact = list.some(m => String(m?.gaBranchOrgId) === String(user.orgId))
                    if (!hasExact && list.length > 0) {
                        const names = list
                            .map(m => (m?.gaBranchOrgName || '').toString().trim())
                            .filter(Boolean)
                        const originLabel = names.length > 0 ? names.join(', ') : '하위 조직/지사'
                        const ok = window.confirm(`${originLabel}에서 생성 및 배정한 매니저입니다. 삭제하시겠습니까?`)
                        if (!ok) return
                    }
                } catch {
                    // 매핑 조회 실패 시에는 기존 동작(삭제 시도) 유지
                }

                const del = await api.delete(`/users/${counterpart.id}/ga-branches/${user.orgId}`)
                const delPayload = (del?.data && del.data.data !== undefined) ? del.data.data : del.data
                if (delPayload && delPayload.deleted === false) {
                    show('이미 배정 해제된 상태입니다', 'success')
                } else {
                    show('배정이 해제되었습니다', 'success')
                }
            } else {
                await api.post(`/users/${counterpart.id}/ga-branches`, { gaBranchOrgId: user.orgId })
                show('담당자로 배정되었습니다', 'success')
            }
            await reload()
            if (affectedCompanyId) {
                await ensureAssignedForCompany(affectedCompanyId, { force: true })
            }
        } catch (err) {
            show('처리 실패', 'error')
        } finally {
            setSavingAssignment(prev => {
                const next = new Set(prev)
                next.delete(key)
                return next
            })
        }
    }

    const createInsurerUser = async (e) => {
        e.preventDefault()
        if (!isGaUser || !user?.orgId || !selectedInsurerId) return
        if (!canManageGaInsurerCounterparts) return show('권한이 없습니다', 'error')
        try {
            // 생성은 "회사 노드"가 아니라, 실제 매핑된 원수사 지점/운영단위에 귀속되어야
            // /counterparts 조회 범위(매핑 기반)에도 포함되어 생성 직후 바로 노출된다.
            const effectiveInsurerOrgId = Number(selectedInsurerMappingOrgId || selectedInsurerId)
            const role = createInsurerUserForm.role
            const hasEmail = Boolean(createInsurerUserForm.email && String(createInsurerUserForm.email).trim())
            if (hasEmail) {
                const roles = [role]
                await api.post('/users', {
                    name: createInsurerUserForm.name,
                    email: String(createInsurerUserForm.email).trim(),
                    password: createInsurerUserForm.password,
                    orgId: effectiveInsurerOrgId,
                    roles,
                    phoneNumber: createInsurerUserForm.phoneNumber,
                })
            } else {
                await api.post('/users/proxy-insurer-managers', {
                    insurerOrgId: effectiveInsurerOrgId,
                    name: createInsurerUserForm.name,
                    phoneNumber: createInsurerUserForm.phoneNumber,
                    role,
                    gaBranchOrgId: createInsurerUserForm.gaBranchOrgId ? Number(createInsurerUserForm.gaBranchOrgId) : undefined,
                })
            }
            show('원수사 담당자가 생성되었습니다', 'success')
            setCreateInsurerUserOpen(false)
            setCreateInsurerUserForm({ name: '', email: '', phoneNumber: '', role: 'INSURER_OPERATION_MANAGER', password: '', gaBranchOrgId: '' })
            await reload()
            if (selectedInsurerId) {
                setAssignedByCompanyId(prev => ({ ...prev, [String(selectedInsurerId)]: true }))
                await ensureAssignedForCompany(selectedInsurerId, { force: true })
            }
        } catch (err) {
            show('생성 실패', 'error')
        }
    }

    // 즐겨찾기 토글
    const toggleFavorite = async (counterpartId) => {
        try {
            const isFavorited = favorites.has(counterpartId)
            if (isFavorited) {
                await api.delete(`/counterparts/${counterpartId}/favorite`)
                setFavorites(prev => {
                    const newSet = new Set(prev)
                    newSet.delete(counterpartId)
                    return newSet
                })
                show('즐겨찾기에서 제거되었습니다', 'success')
            } else {
                await api.post(`/counterparts/${counterpartId}/favorite`)
                setFavorites(prev => new Set([...prev, counterpartId]))
                show('즐겨찾기에 추가되었습니다', 'success')
            }
        } catch (err) {
            show('즐겨찾기 처리 실패', 'error')
        }
    }

    // 메모 저장
    const saveNote = async (counterpartId, note) => {
        try {
            await api.post(`/counterparts/notes/${counterpartId}`, { note })
            setUserNotes(prev => ({ ...prev, [counterpartId]: note }))
            show('메모가 저장되었습니다', 'success')
        } catch (err) {
            show('메모 저장 실패', 'error')
        }
    }

    // NOTE(정책): 설계 의뢰 생성 채널은 "설계관리" 메뉴로 일원화.
    // - 따라서 원수사/담당자 탭에서는 설계의뢰 진입 버튼을 숨긴다.
    // const startRequest = (counterpart) => { ... }

    // NOTE: 공지 보기 기능은 UX 확정 시 활성화(현재 미사용)

    // 상태별 스타일
    const getStatusStyle = (status) => {
        switch (status) {
            case 'ON_DUTY':
                return 'bg-green-100 text-green-800'
            case 'TRAINING':
                return 'bg-blue-100 text-blue-800'
            case 'VACATION':
                return 'bg-yellow-100 text-yellow-800'
            case 'AWAY':
                return 'bg-red-100 text-red-800'
            default:
                return 'bg-gray-100 text-gray-800'
        }
    }

    // 정렬된 데이터
    const sortedCounterparts = useMemo(() => {
        const filtered = counterparts.filter(cp => {
            // 즐겨찾기 우선 표시 옵션 (필터에서 제어)
            return true
        })

        return filtered.sort((a, b) => {
            switch (sortBy) {
                case 'responseTime':
                    return (a.avgResponseTime || 0) - (b.avgResponseTime || 0)
                case 'recentActivity':
                    return new Date(b.lastActivityAt || 0) - new Date(a.lastActivityAt || 0)
                case 'completionCount':
                    return (b.completionCount || 0) - (a.completionCount || 0)
                case 'name':
                    return a.name.localeCompare(b.name)
                default:
                    return 0
            }
        })
    }, [counterparts, sortBy])

    const secondLabel = isGaUser ? '원수사 담당자' : (isInsurerUser ? 'GA 사용자' : '담당자')
    const subtitle = isGaUser
        ? '우리 GA에 매핑된 원수사 담당자 목록'
        : (isInsurerUser
            ? '우리 조직이 담당하는 GA 사용자 목록'
            : (isOperator
                ? (operatorViewMode === 'ALL'
                    ? '전체 원수사 담당자 목록(주의: 로딩이 오래 걸릴 수 있음)'
                    : (operatorViewMode === 'BY_INSURER'
                        ? (operatorInsurerOrgId ? '선택 원수사의 담당자 목록' : '원수사를 선택해주세요')
                        : (operatorViewOrgId ? '선택 GA 조직의 원수사 담당자 목록' : 'GA 조직을 선택해주세요')))
                : '담당자 목록'))

    // 모든 조직 정보를 한 번 로딩(회사명 계산용)
    const [allOrgs, setAllOrgs] = useState([])

    useEffect(() => {
        let ignore = false
        async function loadAllOrgs() {
            try {
                const res = await api.get('/orgs/options', { params: { scope: 'all' } })
                const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
                if (!ignore) setAllOrgs(Array.isArray(payload) ? payload : [])
            } catch {
                if (!ignore) setAllOrgs([])
            }
        }
        loadAllOrgs()
        return () => { ignore = true }
    }, [])

    const gaRootOptionsForOperator = useMemo(() => {
        return allOrgs
            .filter(o => o && String(o.type).toUpperCase() === 'GA')
            .filter(o => o.parentOrgId == null)
    }, [allOrgs])

    const gaLevel2OptionsForOperator = useMemo(() => {
        if (!operatorGaL1) return []
        const pid = Number(operatorGaL1)
        return allOrgs
            .filter(o => o && String(o.type).toUpperCase() === 'GA')
            .filter(o => Number(o.parentOrgId) === pid)
    }, [allOrgs, operatorGaL1])

    const gaLevel3OptionsForOperator = useMemo(() => {
        if (!operatorGaL2) return []
        const pid = Number(operatorGaL2)
        return allOrgs
            .filter(o => o && String(o.type).toUpperCase() === 'GA')
            .filter(o => Number(o.parentOrgId) === pid)
    }, [allOrgs, operatorGaL2])

    const orgById = useMemo(() => {
        const map = {}
        allOrgs.forEach(o => {
            if (o && o.id != null) map[o.id] = o
        })
        return map
    }, [allOrgs])

    const isInsurerCompanyNode = useCallback((org) => {
        if (!org) return false
        if (org.type !== 'INSURER') return false
        const p = org.parentOrgId ? orgById[org.parentOrgId] : null
        if (!p) return false
        const gp = p.parentOrgId ? orgById[p.parentOrgId] : null
        return !gp
    }, [orgById])

    const insurerCompanies = useMemo(() => {
        return allOrgs
            .filter(o => o && o.type === 'INSURER')
            .filter(o => isInsurerCompanyNode(o))
    }, [allOrgs, isInsurerCompanyNode])

    const resolveCompanyId = useCallback((orgId) => {
        const visited = new Set()
        let cur = orgById[orgId]
        let guard = 0
        while (cur && !visited.has(cur.id) && guard++ < 50) {
            visited.add(cur.id)
            if (isInsurerCompanyNode(cur)) return cur.id
            cur = cur.parentOrgId ? orgById[cur.parentOrgId] : null
        }
        return orgId
    }, [orgById, isInsurerCompanyNode])

    // 선택된 보험사(회사) 탭에 대응하는 "실제 매핑된 원수사 조직(지점/운영단위)"을 찾는다.
    // - 모드 전환은 insurer_ga_mappings(ga_org_id, insurer_org_id) 행을 찾아 업데이트해야 하므로 회사 id로는 안 되고,
    //   회사 하위에서 실제로 매핑되어 있는 orgId를 사용해야 한다.
    const selectedMappedInsurerOrg = useMemo(() => {
        if (!isGaUser || !selectedInsurerId) return null
        // orgOptions는 "내 GA에 매핑된 insurer org" 목록
        return orgOptions.find(o => o?.id && resolveCompanyId(o.id) === selectedInsurerId) || null
    }, [isGaUser, selectedInsurerId, orgOptions, resolveCompanyId])

    const selectedManagerAssignmentMode = String(selectedMappedInsurerOrg?.managerAssignmentMode || 'INSURER_MANAGED')
    const selectedInsurerMappingOrgId = selectedMappedInsurerOrg?.id || null

    const getInsurerDisplayName = (orgId, fallbackName) => {
        const visited = new Set()
        let cur = orgById[orgId]
        const insurers = []
        while (cur && !visited.has(cur.id)) {
            visited.add(cur.id)
            if (cur.type === 'INSURER') {
                insurers.push(cur)
            }
            cur = cur.parentOrgId ? orgById[cur.parentOrgId] : null
        }
        if (insurers.length > 0) {
            // 맨 위 루트(예: '손해보험', '생명보험')가 아니라 실제 회사명(삼성화재 등)을 우선 사용
            const companies = insurers.filter(o => o.name !== '손해보험' && o.name !== '생명보험')
            const target = (companies.length > 0 ? companies[companies.length - 1] : insurers[insurers.length - 1])
            return target?.name || fallbackName || ''
        }
        return fallbackName || ''
    }

    const getInsurerRootCategory = useCallback((orgId) => {
        const visited = new Set()
        let cur = orgById[orgId]
        while (cur && !visited.has(cur.id)) {
            visited.add(cur.id)
            // 보험사 카테고리 루트(손해보험/생명보험)를 기준으로 분류
            if (cur.type === 'INSURER') {
                if (cur.name === '손해보험') return 'NONLIFE'
                if (cur.name === '생명보험') return 'LIFE'
            }
            cur = cur.parentOrgId ? orgById[cur.parentOrgId] : null
        }
        return null
    }, [orgById])

    const formatDateYmd = () => {
        const d = new Date()
        const yyyy = String(d.getFullYear())
        const mm = String(d.getMonth() + 1).padStart(2, '0')
        const dd = String(d.getDate()).padStart(2, '0')
        return `${yyyy}${mm}${dd}`
    }

    // GA: 보험사(회사) 목록을 생명/손해로 분리 (매핑 여부와 무관)
    const lifeInsurers = useMemo(
        () => insurerCompanies.filter(o => getInsurerRootCategory(o.id) === 'LIFE'),
        [insurerCompanies, getInsurerRootCategory]
    )
    const nonLifeInsurers = useMemo(
        () => insurerCompanies.filter(o => getInsurerRootCategory(o.id) === 'NONLIFE'),
        [insurerCompanies, getInsurerRootCategory]
    )

    // 카테고리/선택된 원수사 초기화
    useEffect(() => {
        if (!isGaUser) return
        const currentList = insurerCategory === 'LIFE'
            ? lifeInsurers
            : (insurerCategory === 'NONLIFE' ? nonLifeInsurers : insurerCompanies)
        if (currentList.length === 0) {
            setSelectedInsurerId(null)
            return
        }
        // 선택된 원수사가 현재 카테고리에 없으면 첫번째로 교체
        if (!selectedInsurerId || !currentList.some(o => o.id === selectedInsurerId)) {
            setSelectedInsurerId(currentList[0].id)
        }
    }, [isGaUser, insurerCategory, lifeInsurers, nonLifeInsurers, insurerCompanies, selectedInsurerId])

    // GA: 탭에 "미배정" 표기를 위해 배정 여부를 백그라운드로 확인(캐시)
    useEffect(() => {
        if (!isGaUser) return
        const currentList = insurerCategory === 'LIFE'
            ? lifeInsurers
            : (insurerCategory === 'NONLIFE' ? nonLifeInsurers : insurerCompanies)
        let ignore = false
        ; (async () => {
            const ids = currentList.map(o => (o?.id != null ? String(o.id) : null)).filter(Boolean)
            const missing = ids.filter(id => !Object.prototype.hasOwnProperty.call(assignedByCompanyId, id))
            const slice = missing.slice(0, 60)
            if (ignore) return
            await ensureAssignedBatch(slice)
        })()
        return () => { ignore = true }
    }, [isGaUser, insurerCategory, lifeInsurers, nonLifeInsurers, insurerCompanies, assignedByCompanyId, ensureAssignedBatch])

    // 선택된 원수사에 따른 담당자 필터
    const visibleCounterparts = useMemo(() => {
        if (isGaUser) {
            if (gaViewMode === 'ALL_LIST') return sortedCounterparts
            if (!selectedInsurerId) return []
            const isUnderCompany = (orgId, companyId) => {
                const visited = new Set()
                let cur = orgById[orgId]
                let guard = 0
                while (cur && !visited.has(cur.id) && guard++ < 50) {
                    visited.add(cur.id)
                    if (cur.id === companyId) return true
                    cur = cur.parentOrgId ? orgById[cur.parentOrgId] : null
                }
                return false
            }
            return sortedCounterparts.filter(cp => cp?.orgId && isUnderCompany(cp.orgId, selectedInsurerId))
        }
        if (isInsurerUser && orgFilter !== 'ALL') {
            const orgIdNum = Number(orgFilter)
            return sortedCounterparts.filter(cp => cp.orgId === orgIdNum)
        }
        return sortedCounterparts
    }, [isGaUser, isInsurerUser, selectedInsurerId, orgFilter, sortedCounterparts, gaViewMode, orgById])

    // NOTE: activeTabOrgId는 현재 UI에서는 사용하지 않음(필요 시 부활)

    // ===== GA: 전체 원수사 보기 페이징 =====
    const allListPagingEnabled = isGaUser && gaViewMode === 'ALL_LIST'
    const allListTotal = allListPagingEnabled ? visibleCounterparts.length : visibleCounterparts.length
    const allListSize = allListPagingEnabled ? Math.max(1, Number(allListPageSize) || 50) : visibleCounterparts.length
    const allListTotalPages = allListPagingEnabled ? Math.max(1, Math.ceil(allListTotal / allListSize)) : 1
    const allListPageSafe = allListPagingEnabled ? Math.min(Math.max(1, allListPage), allListTotalPages) : 1
    const allListSlice = useMemo(() => {
        if (!allListPagingEnabled) return visibleCounterparts
        const start = (allListPageSafe - 1) * allListSize
        const end = Math.min(start + allListSize, allListTotal)
        return visibleCounterparts.slice(start, end)
    }, [allListPagingEnabled, visibleCounterparts, allListPageSafe, allListSize, allListTotal])

    // 총 페이지 수 변경 등으로 페이지가 범위를 벗어나면 자동 보정
    useEffect(() => {
        if (!allListPagingEnabled) return
        if (allListPage !== allListPageSafe) setAllListPage(allListPageSafe)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allListPagingEnabled, allListPageSafe, allListTotalPages])

    const cards = (
        <div className="p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {visibleCounterparts.map(counterpart => (
                <div key={counterpart.id} className="rounded border p-4 hover:shadow-md transition-shadow">
                    {/* 헤더 */}
                    <div className="flex items-start justify-between mb-3">
                        <div>
                            <h3 className="font-semibold text-lg">{counterpart.name}</h3>
                            <p className="text-sm text-slate-600">{counterpart.position}</p>
                            <p className="text-xs text-slate-500">
                                {(() => {
                                    const company = getInsurerDisplayName(counterpart.orgId, counterpart.orgName)
                                    const orgName = counterpart.orgName || ''
                                    if (!company) return orgName || '-'
                                    if (!orgName) return company
                                    // 회사명이 이미 소속명에 포함되면 중복 표기 방지
                                    if (String(orgName).includes(String(company))) return orgName
                                    return `${company} · ${orgName}`
                                })()}
                            </p>
                            <p className="text-xs text-slate-500">
                                {(() => {
                                    const names = Array.isArray(counterpart?.gaBranchOrgNames)
                                        ? counterpart.gaBranchOrgNames.filter(Boolean)
                                        : []
                                    const label = names.length > 0 ? names.join(', ') : (counterpart?.gaBranchOrgName || '')
                                    return label ? `담당 지사: ${label}` : '담당 지사: -'
                                })()}
                            </p>
                            {counterpart.createdSource === 'GA_PROXY' && (
                                <div className="mt-1">
                                    <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[11px]">
                                        GA 대리 생성
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <button
                                className={`p-1 rounded ${favorites.has(counterpart.id) ? 'text-yellow-500' : 'text-gray-400'}`}
                                onClick={() => toggleFavorite(counterpart.id)}
                                title="즐겨찾기"
                            >
                                {favorites.has(counterpart.id) ? '★' : '☆'}
                            </button>
                            {/* 배정/해제 버튼은 카드 하단으로 이동(상세정보 옆) */}
                        </div>
                    </div>

                    {/* 상태 */}
                    <div className="mb-3">
                        <span className={`px-2 py-1 rounded text-xs ${getStatusStyle(counterpart.status)}`}>{AvailabilityLabel[counterpart.status] || counterpart.status}</span>
                        {counterpart.statusPeriod && (
                            <span className="text-xs text-slate-500 ml-2">{counterpart.statusPeriod}</span>
                        )}
                    </div>

                    {/* KPI */}
                    <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                        <div>
                            <span className="text-slate-600">평균 응답</span>
                            <div className="font-medium">{counterpart.avgResponseTime || '—'}시간</div>
                        </div>
                        <div>
                            <span className="text-slate-600">완료 리드타임</span>
                            <div className="font-medium">{counterpart.avgLeadTime || '—'}일</div>
                        </div>
                        <div>
                            <span className="text-slate-600">처리 건수</span>
                            <div className="font-medium">{counterpart.completionCount || 0}건</div>
                        </div>
                        <div>
                            <span className="text-slate-600">보완 요청률</span>
                            <div className="font-medium">{counterpart.revisionRate || '—'}%</div>
                        </div>
                    </div>

                    {/* 연락 정보 */}
                    <div className="text-xs text-slate-600 mb-3">
                        <div className="flex items-center gap-2">
                            <Phone className="h-3.5 w-3.5 text-sgez-primary" aria-hidden="true" />
                            <a
                                href={`tel:${counterpart.phone}`}
                                className="hover:text-brand-navy"
                                title={counterpart.phone || ''}
                            >
                                {counterpart.phone ? counterpart.phone.replace(/(\d{3})(\d{4})(\d{4})/, '$1-****-$3') : '—'}
                            </a>
                        </div>
                        <div className="flex items-center gap-2">
                            <Mail className="h-3.5 w-3.5 text-sgez-primary" aria-hidden="true" />
                            <a
                                href={`mailto:${counterpart.email}`}
                                className="hover:text-brand-navy"
                                title={counterpart.email || ''}
                            >
                                {counterpart.email ? counterpart.email.replace(/(.{2}).*(@.*)/, '$1***$2') : '—'}
                            </a>
                        </div>
                    </div>

                    {/* 액션 버튼 */}
                    <div className="flex gap-2">
                        {canViewCounterpartDetail && (
                            <button
                                className="flex-1 px-3 py-2 border rounded text-sm hover:bg-slate-50"
                                onClick={() => {
                                    setSelectedCounterpart(counterpart)
                                    setNoteDraft(userNotes[counterpart.id] || '')
                                    setDetailEditMode(false)
                                    setDetailEditForm({ name: '', phoneNumber: '' })
                                    setShowDetailModal(true)
                                }}
                            >
                                상세 정보
                            </button>
                        )}
                        {isGaUser && counterpart.assignmentEditable && (
                            <button
                                className="px-3 py-2 border rounded text-sm hover:bg-slate-50 disabled:opacity-50"
                                disabled={savingAssignment.has(counterpart.id) || !counterpart.gaAssigned}
                                onClick={() => toggleGaAssignment(counterpart)}
                                title={counterpart.gaAssigned ? '배정 해제' : '배정된 상태에서만 해제할 수 있습니다'}
                            >
                                배정해제
                            </button>
                        )}
                    </div>

                    {/* GA 전용 메모 */}
                    {userNotes[counterpart.id] && (
                        <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                            <div className="font-medium text-yellow-800 mb-1">GA 전용 메모</div>
                            <div className="text-yellow-700">{userNotes[counterpart.id]}</div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    )

    const listTable = (
        <div className="mt-6 overflow-x-auto">
            {isGaUser && gaViewMode === 'ALL_LIST' && (
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-sm text-slate-600">
                    <div>
                        총 <span className="font-medium text-slate-800">{visibleCounterparts.length}</span>명
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-slate-500">페이지 크기</span>
                        <select
                            className="rounded border px-2 py-1 text-sm"
                            value={String(allListPageSize)}
                            onChange={(e) => {
                                const next = Number(e.target.value)
                                setAllListPageSize(Number.isFinite(next) && next > 0 ? next : 50)
                                setAllListPage(1)
                            }}
                        >
                            <option value="20">20</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                </div>
            )}
            <table className="min-w-[900px] w-full text-sm">
                <thead>
                    <tr className="text-left border-b bg-slate-50">
                        {isGaUser && <th className="px-3 py-2">보험사</th>}
                        <th className="px-3 py-2">소속</th>
                        <th className="px-3 py-2">이름</th>
                        <th className="px-3 py-2">직책</th>
                        <th className="px-3 py-2">상태</th>
                        <th className="px-3 py-2">연락처</th>
                        <th className="px-3 py-2">이메일</th>
                        <th className="px-3 py-2">액션</th>
                    </tr>
                </thead>
                <tbody>
                    {allListSlice.length === 0 ? (
                        <tr><td className="px-3 py-4 text-slate-500" colSpan={isGaUser ? 8 : 7}>표시할 항목이 없습니다.</td></tr>
                    ) : (
                        allListSlice.map(cp => (
                            <tr key={cp.id} className="border-b hover:bg-slate-50">
                                {isGaUser && (
                                    <td className="px-3 py-2">
                                        {getInsurerDisplayName(cp.orgId, cp.orgName)}
                                    </td>
                                )}
                                <td className="px-3 py-2">{cp.orgName || '-'}</td>
                                <td className="px-3 py-2 font-medium">{cp.name || '-'}</td>
                                <td className="px-3 py-2">{cp.position || '-'}</td>
                                <td className="px-3 py-2">
                                    <span className={`px-2 py-1 rounded text-xs ${getStatusStyle(cp.status)}`}>
                                        {AvailabilityLabel[cp.status] || cp.status || '-'}
                                    </span>
                                </td>
                                <td className="px-3 py-2">{cp.phone || '-'}</td>
                                <td className="px-3 py-2">{cp.email || '-'}</td>
                                <td className="px-3 py-2">
                                    <div className="flex gap-2">
                                        {canViewCounterpartDetail && (
                                            <button
                                                className="rounded border px-2 py-1 text-xs hover:bg-slate-50"
                                                onClick={() => {
                                                    setSelectedCounterpart(cp)
                                                    setNoteDraft(userNotes[cp.id] || '')
                                                    setDetailEditMode(false)
                                                    setDetailEditForm({ name: '', phoneNumber: '' })
                                                    setShowDetailModal(true)
                                                }}
                                            >
                                                상세정보
                                            </button>
                                        )}
                                        {isGaUser && cp.assignmentEditable && (
                                            <button
                                                className="rounded border px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-50"
                                                disabled={savingAssignment.has(cp.id) || !cp.gaAssigned}
                                                onClick={() => toggleGaAssignment(cp)}
                                                title={cp.gaAssigned ? '배정 해제' : '배정된 상태에서만 해제할 수 있습니다'}
                                            >
                                                배정해제
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>

            {isGaUser && gaViewMode === 'ALL_LIST' && (
                <div className="mt-3 flex items-center justify-between text-sm text-slate-600">
                    <div>
                        페이지 <span className="font-medium text-slate-800">{allListPageSafe}</span> / {allListTotalPages}
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            className="rounded border px-3 py-1 disabled:opacity-50"
                            disabled={allListPageSafe <= 1}
                            onClick={() => setAllListPage(1)}
                            title="첫 페이지"
                        >
                            처음
                        </button>
                        <button
                            type="button"
                            className="rounded border px-3 py-1 disabled:opacity-50"
                            disabled={allListPageSafe <= 1}
                            onClick={() => setAllListPage(p => Math.max(1, p - 1))}
                        >
                            이전
                        </button>
                        <button
                            type="button"
                            className="rounded border px-3 py-1 disabled:opacity-50"
                            disabled={allListPageSafe >= allListTotalPages}
                            onClick={() => setAllListPage(p => p + 1)}
                        >
                            다음
                        </button>
                        <button
                            type="button"
                            className="rounded border px-3 py-1 disabled:opacity-50"
                            disabled={allListPageSafe >= allListTotalPages}
                            onClick={() => setAllListPage(allListTotalPages)}
                            title="마지막 페이지"
                        >
                            마지막
                        </button>
                    </div>
                </div>
            )}
        </div>
    )

    const downloadVisibleAsCsv = () => {
        const deriveRoleCode = (positionLabel) => {
            const p = String(positionLabel || '')
            // 정책: 레거시 "교육" 역할은 신규 체계에서 매니저로 흡수
            if (p.includes('교육')) return 'INSURER_OPERATION_MANAGER'
            if (p.includes('플랜')) return 'INSURER_PLANNER'
            if (p.includes('설계')) return 'INSURER_PLANNER'
            if (p.includes('매니저')) return 'INSURER_OPERATION_MANAGER'
            return ''
        }
        const phoneForExcel = (raw) => {
            const digits = String(raw || '').replace(/[^0-9]/g, '')
            if (!digits) return ''
            // Excel이 숫자로 인식해 앞자리 0을 날리는 것을 막기 위해 텍스트로 강제한다.
            // - CSV에서 '010...' 형태는 Excel에서 010...으로 표시(앞의 '는 숨김)
            return `'${digits}`
        }
        const textForExcel = (raw) => {
            const s = String(raw ?? '').trim()
            if (!s) return ''
            // Excel의 지수 표기/정밀도 손실 방지 (id/orgId 등)
            return `'${s}`
        }
        const header = {
            ...(isGaUser ? { 보험사: '보험사' } : {}),
            소속: '소속',
            이름: '이름',
            직책: '직책',
            상태: '상태',
            연락처: '연락처',
            이메일: '이메일',
            // 업로드/업서트용 보조 컬럼(삭제하지 말 것)
            _id: '_id',
            _orgId: '_orgId',
            _role: '_role',
            _gaBranchOrgId: '_gaBranchOrgId',
        }
        const rows = visibleCounterparts.map(cp => ({
            ...(isGaUser ? { 보험사: getInsurerDisplayName(cp.orgId, cp.orgName) } : {}),
            소속: cp.orgName || '',
            이름: cp.name || '',
            직책: cp.position || '',
            상태: AvailabilityLabel[cp.status] || cp.status || '',
            연락처: phoneForExcel(cp.phone),
            이메일: cp.email || '',
            _id: cp.id != null ? textForExcel(cp.id) : '',
            _orgId: cp.orgId != null ? textForExcel(cp.orgId) : '',
            _role: deriveRoleCode(cp.position),
            _gaBranchOrgId: user?.orgId != null ? textForExcel(user.orgId) : '',
        }))
        const kind = isGaUser ? 'insurer_contacts' : (isInsurerUser ? 'ga_users' : 'partners')
        const suffix = gaViewMode === 'ALL_LIST' ? 'all' : 'filtered'
        downloadCsv(`people_${kind}_${suffix}_${formatDateYmd()}.csv`, [header, ...rows])
    }

    const uploadCounterpartsCsv = async (file) => {
        if (!file) return
        if (!isGaUser) return show('GA 사용자만 업로드할 수 있습니다', 'error')
        if (!canManageGaInsurerCounterparts) return show('권한이 없습니다', 'error')
        try {
            // Excel이 CP949(EUC-KR)로 CSV를 저장하는 케이스가 있어, UTF-8 실패 시 EUC-KR로 재시도한다.
            const buf = await file.arrayBuffer()
            const decode = (encoding) => new TextDecoder(encoding, { fatal: false }).decode(buf)
            let parsed = parseCsv(decode('utf-8'))
            const normalizeKey = (k) =>
                String(k ?? '')
                    .replace(/[\u200B-\u200D\uFEFF]/g, '') // zero-width/BOM
                    .trim()
                    .replace(/\s+/g, '') // 모든 공백 제거
                    .toLowerCase()
            const csvHeaders = Array.isArray(parsed?.headers) ? parsed.headers : []
            const normalizedHeaders = csvHeaders.map(normalizeKey)
            const hasAnyExpectedHeader =
                normalizedHeaders.includes(normalizeKey('이름'))
                || normalizedHeaders.includes(normalizeKey('연락처'))
                || normalizedHeaders.includes(normalizeKey('이메일'))
                || normalizedHeaders.includes(normalizeKey('_id'))
                || normalizedHeaders.includes(normalizeKey('_orgId'))
            if (!hasAnyExpectedHeader) {
                // fallback: EUC-KR (CP949 계열)
                parsed = parseCsv(decode('euc-kr'))
            }
            const rows = Array.isArray(parsed?.rows) ? parsed.rows : []
            if (rows.length === 0) {
                show('CSV에 데이터가 없습니다', 'error')
                return
            }

            const pick = (obj, keys) => {
                for (const k of keys) {
                    if (obj && Object.prototype.hasOwnProperty.call(obj, k)) return obj[k]
                }
                // 헤더에 NBSP/제로폭 문자 등이 섞여 키 매칭이 실패하는 케이스를 보정한다.
                const targetNorms = keys.map(normalizeKey)
                if (obj && typeof obj === 'object') {
                    for (const ek of Object.keys(obj)) {
                        const ekNorm = normalizeKey(ek)
                        for (const tn of targetNorms) {
                            if (ekNorm && tn && ekNorm === tn) return obj[ek]
                        }
                    }
                }
                return ''
            }
            const toNum = (v) => {
                let s = String(v ?? '').trim()
                if (s.startsWith("'")) s = s.slice(1)
                if (!s) return null
                // 1,234 형태 방지
                s = s.replace(/,/g, '')
                const n = Number(s)
                return Number.isFinite(n) ? n : null
            }
            const normalizePhone = (v) => {
                let s = String(v ?? '').trim()
                // 다운로드 CSV에서 Excel 텍스트 강제용으로 앞에 '를 붙이므로 업로드 시 제거
                if (s.startsWith("'")) s = s.slice(1)
                s = s.replace(/[^0-9]/g, '')
                // Excel이 앞의 0을 제거한 채로 저장한 경우 보정(한국 전화번호는 통상 0으로 시작)
                if (s && !s.startsWith('0')) s = '0' + s
                return s || null
            }
            const headers = Array.isArray(parsed?.headers) ? parsed.headers : []
            const hasRoleCol = headers.includes('_role') || headers.includes('role') || headers.includes('역할')
            const deriveRole = (row) => {
                // CSV에 role 컬럼이 있으면 "그 값만" 신뢰한다. (비어있으면 role=null로 무시)
                if (hasRoleCol) {
                    const explicit = String(pick(row, ['_role', 'role', '역할']) || '').trim()
                    return explicit || null
                }
                // 예전 CSV(역할 컬럼 없음) 호환: 직책에서만 추정
                const pos = String(pick(row, ['직책']) || '').trim()
                // 정책: 레거시 "교육" 역할은 신규 체계에서 매니저로 흡수
                if (pos.includes('교육')) return 'INSURER_OPERATION_MANAGER'
                if (pos.includes('플랜') || pos.includes('설계')) return 'INSURER_PLANNER'
                if (pos.includes('매니저')) return 'INSURER_OPERATION_MANAGER'
                return null
            }

            const resolveOrgIdFromLabels = (companyLabel, orgLabel) => {
                const companyName = String(companyLabel || '').trim()
                const orgName = String(orgLabel || '').trim()
                if (!orgName) return null

                const list = Array.isArray(allOrgs) ? allOrgs : []
                const byId = orgById || {}
                const parentToChildren = new Map()
                list.forEach(o => {
                    if (!o || o.id == null || o.parentOrgId == null) return
                    const key = String(o.parentOrgId)
                    const arr = parentToChildren.get(key) || []
                    arr.push(o)
                    parentToChildren.set(key, arr)
                })

                const isInsurerCompany = (o) => {
                    if (!o || o.id == null) return false
                    if (String(o.type) !== 'INSURER') return false
                    // CounterpartDirectory 내부의 isInsurerCompanyNode 기준과 동일 개념(부모가 있고 조부모가 없는 INSURER)
                    const p = o.parentOrgId != null ? byId[o.parentOrgId] : null
                    if (!p) return false
                    const gp = p.parentOrgId != null ? byId[p.parentOrgId] : null
                    return !gp
                }

                const companies = companyName
                    ? list.filter(isInsurerCompany).filter(o => String(o.name || '').trim() === companyName)
                    : []

                const findOrgUnder = (rootId) => {
                    const out = []
                    const seen = new Set()
                    const queue = [String(rootId)]
                    while (queue.length > 0) {
                        const pid = queue.shift()
                        const kids = parentToChildren.get(pid) || []
                        for (const k of kids) {
                            if (!k || k.id == null) continue
                            const kidId = String(k.id)
                            if (seen.has(kidId)) continue
                            seen.add(kidId)
                            if (String(k.type) === 'INSURER' && String(k.name || '').trim() === orgName) {
                                out.push(k)
                            }
                            queue.push(kidId)
                        }
                    }
                    if (out.length === 1) return out[0].id
                    return null
                }

                if (companies.length === 1) {
                    const found = findOrgUnder(companies[0].id)
                    if (found != null) return found
                }

                // fallback: orgName만으로 전체에서 매칭(유일할 때만)
                const allMatches = list
                    .filter(o => o && o.id != null && String(o.type) === 'INSURER')
                    .filter(o => String(o.name || '').trim() === orgName)
                if (allMatches.length === 1) return allMatches[0].id

                return null
            }

            const payload = rows.map((r) => {
                const companyLabel = pick(r, ['보험사', 'insurer', 'company'])
                const orgLabel = pick(r, ['소속', 'org', 'branch'])
                const rawOrgId = toNum(pick(r, ['_orgId', 'orgId']))
                const effectiveOrgId = (rawOrgId != null && orgById && orgById[rawOrgId])
                    ? rawOrgId
                    : (rawOrgId != null ? rawOrgId : null)

                const resolvedOrgId = (effectiveOrgId != null && orgById && orgById[effectiveOrgId])
                    ? effectiveOrgId
                    : resolveOrgIdFromLabels(companyLabel, orgLabel)

                return ({
                    id: toNum(pick(r, ['_id', 'id'])),
                    orgId: resolvedOrgId,
                    email: String(pick(r, ['이메일', 'email']) || '').trim() || null,
                    name: String(pick(r, ['이름', 'name']) || '').trim() || null,
                    phoneNumber: normalizePhone(pick(r, ['연락처', 'phoneNumber', 'phone'])),
                    role: deriveRole(r),
                    gaBranchOrgId: toNum(pick(r, ['_gaBranchOrgId', 'gaBranchOrgId'])) || (user?.orgId != null ? Number(user.orgId) : null),
                })
            })

            // CSV 파싱 실패(헤더/구분자/인코딩 문제) 탐지: name/phone이 대부분 비면 안내
            const empties = payload.filter(x => !x?.name && !x?.phoneNumber).length
            if (payload.length > 0 && empties / payload.length > 0.7) {
                // eslint-disable-next-line no-console
                console.warn('CSV parse looks broken:', { delimiter: parsed?.delimiter, headers: parsed?.headers?.slice?.(0, 20) })
                show('CSV 헤더를 인식하지 못했습니다. 엑셀에서 “CSV UTF-8(쉼표로 분리)”로 다시 저장해보세요. (또는 메모장으로 열어 쉼표 구분인지 확인)', 'error')
                return
            }

            // orgId가 비어있는 행이 많으면(특히 타 환경 CSV) 신규 생성이 실패할 수 있어, 미리 경고한다.
            const missingOrg = payload.filter(x => x?.orgId == null).length
            if (payload.length > 0 && missingOrg > 0) {
                show(`주의: 조직(_orgId)을 해석하지 못한 행이 ${missingOrg}건 있습니다. (보험사/소속명으로 자동 매핑을 시도했지만 실패) 해당 행은 신규 생성 시 실패할 수 있습니다.`, 'error')
            }

            // 간단한 클라이언트 검증(생성 row에 orgId 누락 방지)
            const hasAnyNewWithoutOrg = payload.some(x => !x?.id && !x?.email && !x?.orgId)
            if (hasAnyNewWithoutOrg) {
                show('신규 생성 행에 _orgId가 없습니다. 최신 CSV를 다시 다운로드해서 사용해주세요.', 'error')
                return
            }
            setImportPayload(payload)
            setImportPreviewOpen(true)
            setImportPreview(null)
            setImportPreviewLoading(true)
            try {
                const res = await api.post('/users/import-insurer-managers?dryRun=true', payload)
                const data = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
                setImportPreview(data || null)
            } finally {
                setImportPreviewLoading(false)
            }
        } catch (err) {
            const msg =
                err?.response?.data?.message
                || err?.response?.data?.msg
                || 'CSV 업로드에 실패했습니다.'
            show(msg, 'error')
        }
    }

    const applyImport = async () => {
        if (!importPayload || !Array.isArray(importPayload) || importPayload.length === 0) {
            show('업로드할 데이터가 없습니다', 'error')
            return
        }
        setImportApplying(true)
        try {
            const res = await api.post('/users/import-insurer-managers', importPayload)
            const data = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const created = data?.created ?? 0
            const updated = data?.updated ?? 0
            const skipped = data?.skippedNoChange ?? 0
            const errCount = data?.errorCount ?? 0
            if (errCount > 0) {
                show(`업로드 완료: 생성 ${created} / 수정 ${updated} (에러 ${errCount}건)`, 'error')
                // eslint-disable-next-line no-console
                console.warn('CSV import errors:', data?.errors)
            } else if ((created + updated) === 0) {
                show('업로드가 완료되었지만 반영된 항목이 없습니다. (식별자 매칭 실패 또는 변경사항 없음)', 'error')
            } else {
                const extra = skipped > 0 ? ` / 변경없음 ${skipped}` : ''
                show(`업로드 완료: 생성 ${created} / 수정 ${updated}${extra}`, 'success')
            }
            // eslint-disable-next-line no-console
            console.info('CSV import result:', data)
            setImportPreviewOpen(false)
            setImportPayload(null)
            setImportPreview(null)
            await reload()
        } catch (err) {
            const msg =
                err?.response?.data?.message
                || err?.response?.data?.msg
                || 'CSV 업로드에 실패했습니다.'
            show(msg, 'error')
        } finally {
            setImportApplying(false)
        }
    }

    return (
        <section>
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">사용자/담당자</h1>
                    <div className="mt-2">
                        <PeopleTabs />
                    </div>
                    <p className="text-slate-600 mt-3">{secondLabel} · {subtitle}</p>
                </div>
                <div className="flex items-center gap-2">
                    {isGaUser && gaViewMode !== 'ALL_LIST' && (
                        <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={() => setGaViewMode('ALL_LIST')}>
                            전체 원수사 보기
                        </button>
                    )}
                    {isGaUser && gaViewMode === 'ALL_LIST' && (
                        <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={() => setGaViewMode('CARDS')}>
                            카드로 보기
                        </button>
                    )}
                    {isGaUser && canManageGaInsurerCounterparts && (
                        <div className="relative flex items-center gap-2" data-csv-help>
                            <label className="rounded border px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer">
                                CSV 업로드
                                <input
                                    type="file"
                                    accept=".csv,text/csv"
                                    className="hidden"
                                    onChange={(e) => {
                                        const f = e.target.files && e.target.files[0]
                                        // 같은 파일을 연속 업로드할 수 있도록 value 초기화
                                        e.target.value = ''
                                        uploadCounterpartsCsv(f)
                                    }}
                                />
                            </label>
                            <button
                                type="button"
                                className="h-9 w-9 inline-flex items-center justify-center rounded border text-sm text-slate-700 hover:bg-slate-50"
                                onClick={() => setCsvHelpOpen(v => !v)}
                                title="CSV 업로드 도움말"
                                aria-label="CSV 업로드 도움말"
                            >
                                ?
                            </button>
                            {csvHelpOpen && (
                                <div className="absolute right-0 top-11 z-50 w-[420px] max-w-[90vw] rounded border bg-white shadow-lg p-4 text-sm">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="font-semibold text-slate-900">CSV 업로드 도움말</div>
                                        <button
                                            type="button"
                                            className="text-slate-500 hover:text-slate-700"
                                            onClick={() => setCsvHelpOpen(false)}
                                            aria-label="닫기"
                                        >
                                            ✕
                                        </button>
                                    </div>

                                    <div className="mt-3 space-y-3 text-slate-700">
                                        <div>
                                            <div className="font-medium text-slate-900">수정 가능한 필드</div>
                                            <ul className="mt-1 list-disc pl-5 text-[13px] space-y-1">
                                                <li><span className="font-medium">이름</span>, <span className="font-medium">연락처</span></li>
                                            </ul>
                                        </div>

                                        <div>
                                            <div className="font-medium text-slate-900">수정하면 안 되는(식별자) 필드</div>
                                            <ul className="mt-1 list-disc pl-5 text-[13px] space-y-1">
                                                <li><span className="font-medium">`_id`</span>, <span className="font-medium">`이메일`</span>, <span className="font-medium">`_orgId`</span>, <span className="font-medium">`_role`</span>, <span className="font-medium">`_gaBranchOrgId`</span></li>
                                                <li>이 값이 바뀌면 업로드가 거부되거나, 다른 계정이 수정/생성될 수 있습니다.</li>
                                            </ul>
                                        </div>

                                        <div>
                                            <div className="font-medium text-slate-900">권한/대상</div>
                                            <ul className="mt-1 list-disc pl-5 text-[13px] space-y-1">
                                                <li><span className="font-medium">GA_PROXY</span>로 생성된 원수사 담당자만 CSV로 수정할 수 있습니다.</li>
                                                <li>또한 <span className="font-medium">우리 GA 조직 범위에 배정</span>된 담당자만 수정 가능합니다.</li>
                                            </ul>
                                        </div>

                                        <div>
                                            <div className="font-medium text-slate-900">엑셀 저장 방법(중요)</div>
                                            <ul className="mt-1 list-disc pl-5 text-[13px] space-y-1">
                                                <li>가능하면 엑셀에서 <span className="font-medium">“CSV UTF-8(쉼표로 분리)”</span>로 저장하세요.</li>
                                                <li>다른 형식(CP949, 구분자 `;`)로 저장되면 헤더를 못 읽어 <span className="font-medium">NAME_EMPTY/PHONE_EMPTY</span>가 뜰 수 있습니다.</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <button type="button" className="hidden md:inline-flex rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={downloadVisibleAsCsv}>
                        CSV 다운로드
                    </button>
                </div>
            </div>

            {/* 검색 및 필터 */}
            <div className="mt-6 space-y-4">
                <div className="flex flex-wrap gap-3 items-center">
                    <div className="flex-1 min-w-64">
                        <input
                            className="w-full rounded border px-3 py-2 text-sm"
                            placeholder="검색: 이름, 직책, 지점, 원수사명"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <select className="rounded border px-3 py-2 text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="ALL">상태 전체</option>
                        <option value="ON_DUTY">업무중</option>
                        <option value="TRAINING">교육중</option>
                        <option value="VACATION">휴가중</option>
                        <option value="AWAY">부재중</option>
                    </select>
                    {isInsurerUser && (
                        <select className="rounded border px-3 py-2 text-sm" value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)}>
                            <option value="ALL">전체</option>
                            {orgOptions.map(org => (
                                <option key={org.id} value={org.id}>{org.name}</option>
                            ))}
                        </select>
                    )}
                    {isOperator && (
                        <>
                            <select
                                className="rounded border px-3 py-2 text-sm"
                                value={operatorViewMode}
                                onChange={(e) => {
                                    const v = e.target.value || 'ALL'
                                    // 운영자 전체보기는 대량 로딩이 발생할 수 있어, 실수 방지를 위해 확인 팝업을 띄운다.
                                    if (v === 'ALL') {
                                        const ok = window.confirm('전체보기는 모든 원수사 담당자를 불러오므로 로딩이 오래 걸릴 수 있습니다.\n계속 진행할까요?')
                                        if (!ok) {
                                            // 기본 모드로 복귀 (진입 즉시 로딩 방지 정책 유지)
                                            setOperatorViewMode('BY_GA_ORG')
                                            setOperatorViewOrgId('')
                                            setOperatorGaL1('')
                                            setOperatorGaL2('')
                                            setOperatorGaL3('')
                                            setOperatorInsurerOrgId('')
                                            return
                                        }
                                    }
                                    setOperatorViewMode(v)
                                    // 모드 전환 시 조직 선택은 초기화 (의도치 않은 필터 잔존 방지)
                                    setOperatorViewOrgId('')
                                    setOperatorGaL1('')
                                    setOperatorGaL2('')
                                    setOperatorGaL3('')
                                    setOperatorInsurerOrgId('')
                                }}
                                title="운영자: 조회 모드"
                            >
                                <option value="ALL">전체보기</option>
                                <option value="BY_GA_ORG">GA 조직별</option>
                                <option value="BY_INSURER">원수사별</option>
                            </select>

                            {operatorViewMode === 'BY_GA_ORG' && (
                                <>
                                    <select
                                        className="rounded border px-3 py-2 text-sm min-w-44"
                                        value={operatorGaL1}
                                        onChange={(e) => {
                                            const v = e.target.value || ''
                                            setOperatorGaL1(v)
                                            setOperatorGaL2('')
                                            setOperatorGaL3('')
                                            setOperatorViewOrgId(v)
                                        }}
                                        title="운영자: GA 1차 조직"
                                    >
                                        <option value="">GA 1차 조직 선택</option>
                                        {gaRootOptionsForOperator.map(o => (
                                            <option key={String(o.id)} value={String(o.id)}>{o.name}</option>
                                        ))}
                                    </select>

                                    <select
                                        className="rounded border px-3 py-2 text-sm min-w-44"
                                        value={operatorGaL2}
                                        onChange={(e) => {
                                            const v = e.target.value || ''
                                            setOperatorGaL2(v)
                                            setOperatorGaL3('')
                                            setOperatorViewOrgId(v || operatorGaL1)
                                        }}
                                        disabled={!operatorGaL1}
                                        title="운영자: GA 2차 조직"
                                    >
                                        <option value="">{operatorGaL1 ? 'GA 2차 조직(전체)' : '먼저 1차 조직을 선택하세요'}</option>
                                        {gaLevel2OptionsForOperator.map(o => (
                                            <option key={String(o.id)} value={String(o.id)}>{o.name}</option>
                                        ))}
                                    </select>

                                    <select
                                        className="rounded border px-3 py-2 text-sm min-w-44"
                                        value={operatorGaL3}
                                        onChange={(e) => {
                                            const v = e.target.value || ''
                                            setOperatorGaL3(v)
                                            setOperatorViewOrgId(v || operatorGaL2 || operatorGaL1)
                                        }}
                                        disabled={!operatorGaL2}
                                        title="운영자: GA 3차 조직"
                                    >
                                        <option value="">{operatorGaL2 ? 'GA 3차 조직(전체)' : '먼저 2차 조직을 선택하세요'}</option>
                                        {gaLevel3OptionsForOperator.map(o => (
                                            <option key={String(o.id)} value={String(o.id)}>{o.name}</option>
                                        ))}
                                    </select>
                                </>
                            )}

                            {operatorViewMode === 'BY_INSURER' && (
                                <select
                                    className="rounded border px-3 py-2 text-sm min-w-56"
                                    value={operatorInsurerOrgId}
                                    onChange={(e) => setOperatorInsurerOrgId(e.target.value || '')}
                                    title="운영자: 원수사(회사) 선택"
                                >
                                    <option value="">원수사 선택</option>
                                    {insurerCompanies
                                        .slice()
                                        .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')))
                                        .map(o => (
                                            <option key={String(o.id)} value={String(o.id)}>
                                                {o.name}
                                            </option>
                                        ))}
                                </select>
                            )}

                            <label className="inline-flex items-center gap-2 text-sm text-slate-700 select-none">
                                <input
                                    type="checkbox"
                                    className="h-4 w-4"
                                    checked={onlyGaManagerCreated}
                                    onChange={(e) => setOnlyGaManagerCreated(!!e.target.checked)}
                                />
                                GA 관리자가 만든 담당자만
                            </label>
                        </>
                    )}
                    <select className="rounded border px-3 py-2 text-sm" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                        <option value="responseTime">응답속도 빠른 순</option>
                        <option value="recentActivity">최근 활동 순</option>
                        <option value="completionCount">처리건수 많은 순</option>
                        <option value="name">이름 순</option>
                    </select>
                </div>

                {/* GA: 생명/손해 선택 + 원수사 탭 */}
                {isGaUser && gaViewMode !== 'ALL_LIST' && (
                    <div className="space-y-3 pb-2">
                        <div className="inline-flex rounded border bg-white overflow-hidden text-sm">
                            <button
                                type="button"
                                className={`px-4 py-2 border-r ${insurerCategory === 'ALL' ? 'bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10' : 'bg-white text-slate-700'}`}
                                onClick={() => setInsurerCategory('ALL')}
                            >
                                전체
                            </button>
                            <button
                                type="button"
                                className={`px-4 py-2 border-r ${insurerCategory === 'NONLIFE' ? 'bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10' : 'bg-white text-slate-700'}`}
                                onClick={() => setInsurerCategory('NONLIFE')}
                            >
                                손해보험
                            </button>
                            <button
                                type="button"
                                className={`px-4 py-2 ${insurerCategory === 'LIFE' ? 'bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10' : 'bg-white text-slate-700'}`}
                                onClick={() => setInsurerCategory('LIFE')}
                            >
                                생명보험
                            </button>
                        </div>

                        {/* 원수사별 탭 + 공지 보기 버튼 */}
                        <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                            {/* PC: 기존 탭 유지 */}
                            <div className="hidden md:flex flex-wrap gap-2">
                                {(insurerCategory === 'LIFE'
                                    ? lifeInsurers
                                    : (insurerCategory === 'NONLIFE' ? nonLifeInsurers : insurerCompanies)
                                ).map(ins => (
                                    <button
                                        key={ins.id}
                                        type="button"
                                        className={`px-3 py-1 rounded-full border ${selectedInsurerId === ins.id ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10' : 'bg-white text-slate-700 border-slate-300'}`}
                                        onClick={() => {
                                            setSelectedInsurerId(ins.id)
                                            // 탭 선택 시 배정 여부를 즉시 확인(미확인 상태 최소화)
                                            ensureAssignedForCompany(ins.id)
                                        }}
                                    >
                                        {getInsurerDisplayName(ins.id, ins.name)}
                                        {(() => {
                                            const key = String(ins.id)
                                            if (checkingAssignedCompanyIds.has(key)) {
                                                return (
                                                    <span className="ml-2 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[11px]">
                                                        확인중
                                                    </span>
                                                )
                                            }
                                            if (assignedByCompanyId[key] === false) {
                                                return (
                                                    <span className="ml-2 rounded-full bg-rose-100 text-rose-800 px-2 py-0.5 text-[11px]">
                                                        미배정
                                                    </span>
                                                )
                                            }
                                            return null
                                        })()}
                                    </button>
                                ))}
                                {(insurerCategory === 'LIFE'
                                    ? lifeInsurers
                                    : (insurerCategory === 'NONLIFE' ? nonLifeInsurers : insurerCompanies)
                                ).length === 0 && (
                                    <span className="text-xs text-slate-500">매핑된 원수사가 없습니다.</span>
                                )}
                            </div>

                            {/* Mobile: 원수사 목록을 셀렉트로 */}
                            <div className="w-full md:hidden">
                                {(() => {
                                    const list = (insurerCategory === 'LIFE'
                                        ? lifeInsurers
                                        : (insurerCategory === 'NONLIFE' ? nonLifeInsurers : insurerCompanies)
                                    )
                                    if (!Array.isArray(list) || list.length === 0) {
                                        return <div className="text-xs text-slate-500">매핑된 원수사가 없습니다.</div>
                                    }
                                    return (
                                        <select
                                            className="w-full rounded border px-3 py-2 text-sm"
                                            value={selectedInsurerId != null ? String(selectedInsurerId) : ''}
                                            onChange={(e) => {
                                                const v = e.target.value
                                                if (!v) return
                                                const idNum = Number(v)
                                                setSelectedInsurerId(idNum)
                                                ensureAssignedForCompany(idNum)
                                            }}
                                        >
                                            {list.map((ins) => {
                                                const key = String(ins.id)
                                                const baseName = getInsurerDisplayName(ins.id, ins.name)
                                                const suffix = checkingAssignedCompanyIds.has(key)
                                                    ? ' (확인중)'
                                                    : (assignedByCompanyId[key] === false ? ' (미배정)' : '')
                                                return (
                                                    <option key={String(ins.id)} value={String(ins.id)}>
                                                        {baseName}{suffix}
                                                    </option>
                                                )
                                            })}
                                        </select>
                                    )
                                })()}
                            </div>
                            {selectedInsurerId && (
                                <div className="flex flex-col md:flex-row md:items-center gap-2">
                                    {assignedByCompanyId[String(selectedInsurerId)] === false && (
                                        <div className="w-full md:w-auto text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">
                                            담당자가 배정되어 있지 않습니다. 담당자를 생성/배정해주세요.
                                        </div>
                                    )}

                                    {/* 담당자 배정 모드 + 전환 */}
                                    {canManageGaInsurerCounterparts && (
                                        <div className="flex items-center gap-2 text-xs">
                                            <span className="text-slate-500 whitespace-nowrap">담당자 배정</span>
                                            <span className={`font-medium ${selectedManagerAssignmentMode === 'GA_MANAGED' ? 'text-emerald-700' : 'text-slate-700'}`}>
                                                {selectedManagerAssignmentMode === 'GA_MANAGED' ? 'GA 관리' : '원수사 관리'}
                                            </span>
                                            <button
                                                type="button"
                                                className="rounded border px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-50"
                                                disabled={savingMode || !selectedInsurerMappingOrgId}
                                                onClick={() => {
                                                    if (!selectedInsurerMappingOrgId) {
                                                        show('매핑된 원수사가 없어 전환할 수 없습니다. 먼저 원수사- GA 조직 매핑을 추가하세요.', 'error')
                                                        return
                                                    }
                                                    updateManagerAssignmentMode(
                                                        selectedManagerAssignmentMode === 'GA_MANAGED' ? 'INSURER_MANAGED' : 'GA_MANAGED',
                                                        selectedInsurerMappingOrgId
                                                    )
                                                }}
                                                title="해당 원수사의 담당자 배정 주체를 변경합니다"
                                            >
                                                전환
                                            </button>
                                        </div>
                                    )}

                                    {/* 액션 버튼: 모바일은 2열 그리드, PC는 기존 inline */}
                                    <div className="grid grid-cols-2 gap-2 md:flex md:items-center md:gap-2">
                                        {canManageGaInsurerCounterparts && (
                                            <button
                                                type="button"
                                                className="w-full md:w-auto px-3 py-2 md:px-3 md:py-1 rounded border text-xs hover:bg-slate-50 whitespace-nowrap"
                                                onClick={() => setCreateInsurerUserOpen(true)}
                                            >
                                                담당자 생성
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="w-full md:w-auto px-3 py-2 md:px-3 md:py-1 rounded border text-xs hover:bg-slate-50 whitespace-nowrap"
                                            onClick={() => {
                                                const params = new URLSearchParams()
                                                params.set('targetOrgIds', String(selectedInsurerMappingOrgId || selectedInsurerId))
                                                navigate(`/notices?${params.toString()}`)
                                            }}
                                        >
                                            공지 보기
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* GA: 선택된 원수사(회사) 탭에 담당자 정보가 없을 때 안내 */}
            {isGaUser && gaViewMode !== 'ALL_LIST' && selectedInsurerId && visibleCounterparts.length === 0 && (
                <div className="mt-3 rounded border bg-slate-50 px-4 py-3 text-sm text-slate-700">
                    담당자가 배정되어 있지 않습니다. 담당자를 생성/배정해주세요.
                </div>
            )}

            <div className="mt-1">
                <ListContainer
                    loading={loading}
                    error={error}
                    onRetry={() => reload()}
                    header={null}
                    table={(isInsurerUser || gaViewMode === 'ALL_LIST') ? listTable : cards}
                    footer={null}
                />
            </div>

            {createInsurerUserOpen && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4" onClick={() => setCreateInsurerUserOpen(false)}>
                    <form className="w-full max-w-md rounded bg-white p-6 space-y-4" onClick={(e) => e.stopPropagation()} onSubmit={createInsurerUser}>
                        <h3 className="text-lg font-semibold">원수사 담당자 생성</h3>
                        <p className="text-xs text-slate-500">
                            선택된 원수사에 담당자를 생성합니다. (원수사 관리자가 서비스를 사용 중이면 생성할 수 없습니다.)
                        </p>
                        <div>
                            <label className="block text-sm">이름</label>
                            <input className="mt-1 w-full rounded border px-3 py-2" value={createInsurerUserForm.name} onChange={(e) => setCreateInsurerUserForm(prev => ({ ...prev, name: e.target.value }))} required />
                        </div>
                        <div>
                            <label className="block text-sm">이메일 (선택)</label>
                            <input type="email" className="mt-1 w-full rounded border px-3 py-2" value={createInsurerUserForm.email} onChange={(e) => setCreateInsurerUserForm(prev => ({ ...prev, email: e.target.value }))} placeholder="비워두면 더미 이메일이 자동 생성됩니다" />
                        </div>
                        {!Boolean(createInsurerUserForm.email && String(createInsurerUserForm.email).trim()) && (
                            <div>
                                <label className="block text-sm">배정 지사</label>
                                <select
                                    className="mt-1 w-full rounded border px-3 py-2"
                                    value={createInsurerUserForm.gaBranchOrgId || ''}
                                    onChange={(e) => setCreateInsurerUserForm(prev => ({ ...prev, gaBranchOrgId: e.target.value }))}
                                    required
                                >
                                    {gaBranchOptions.length === 0 ? (
                                        <option value={String(user?.orgId || '')}>내 조직</option>
                                    ) : (
                                        gaBranchOptions.map(o => (
                                            <option key={String(o.id)} value={String(o.id)}>
                                                {o.name}
                                            </option>
                                        ))
                                    )}
                                </select>
                                <p className="text-xs text-slate-500 mt-1">
                                    사업단 계정으로 생성하는 경우, 여기서 선택한 지사에 담당자가 배정됩니다.
                                </p>
                            </div>
                        )}
                        <div>
                            <label className="block text-sm">전화번호</label>
                            <input className="mt-1 w-full rounded border px-3 py-2" value={createInsurerUserForm.phoneNumber} onChange={(e) => setCreateInsurerUserForm(prev => ({ ...prev, phoneNumber: e.target.value }))} required />
                        </div>
                        <div>
                            <label className="block text-sm">역할</label>
                            <select className="mt-1 w-full rounded border px-3 py-2" value={createInsurerUserForm.role} onChange={(e) => setCreateInsurerUserForm(prev => ({ ...prev, role: e.target.value }))}>
                                {/* 정책: 원수사 역할은 4단계만(여기서는 담당자 생성 UX에 맞춰 매니저/설계사만 노출) */}
                                <option value="INSURER_PLANNER">원수사 설계사</option>
                                <option value="INSURER_OPERATION_MANAGER">원수사 매니저</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm">임시 비밀번호 (이메일 직접 입력 시에만 사용)</label>
                            <div className="mt-1 flex gap-2">
                                <input type="text" className="flex-1 rounded border px-3 py-2" value={createInsurerUserForm.password} onChange={(e) => setCreateInsurerUserForm(prev => ({ ...prev, password: e.target.value }))} />
                                <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={() => setCreateInsurerUserForm(prev => ({ ...prev, password: generatePassword() }))}>
                                    재생성
                                </button>
                            </div>
                            <p className="text-xs text-slate-500 mt-1">더미 이메일 자동 생성 모드에서는 로그인 목적이 아니므로 비밀번호는 저장되지 않습니다.</p>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => setCreateInsurerUserOpen(false)}>취소</button>
                            <button type="submit" className="rounded bg-brand-navy text-white px-3 py-2 text-sm">생성</button>
                        </div>
                    </form>
                </div>
            )}

            {/* CSV 업로드 미리보기 모달 */}
            {importPreviewOpen && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4" onClick={() => {
                    if (importApplying) return
                    setImportPreviewOpen(false)
                    setImportPayload(null)
                    setImportPreview(null)
                }}>
                    <div className="w-full max-w-2xl rounded bg-white p-6 max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold">CSV 업로드 미리보기</h3>
                            <button className="text-slate-500 hover:text-slate-700" onClick={() => {
                                if (importApplying) return
                                setImportPreviewOpen(false)
                                setImportPayload(null)
                                setImportPreview(null)
                            }}>✕</button>
                        </div>

                        {importPreviewLoading ? (
                            <div className="text-sm text-slate-600">검증 중입니다…</div>
                        ) : (
                            <div className="space-y-3 text-sm">
                                <div className="rounded border bg-slate-50 px-4 py-3">
                                    <div className="flex flex-wrap gap-x-6 gap-y-1">
                                        <div>총 행: <span className="font-medium">{Array.isArray(importPayload) ? importPayload.length : 0}</span></div>
                                        <div>생성 예정: <span className="font-medium">{importPreview?.created ?? 0}</span></div>
                                        <div>수정 예정: <span className="font-medium">{importPreview?.updated ?? 0}</span></div>
                                        <div>변경 없음: <span className="font-medium">{importPreview?.skippedNoChange ?? 0}</span></div>
                                        <div>에러: <span className={`font-medium ${(importPreview?.errorCount ?? 0) > 0 ? 'text-rose-700' : ''}`}>{importPreview?.errorCount ?? 0}</span></div>
                                    </div>
                                    <p className="text-xs text-slate-500 mt-2">
                                        ‘확인’을 누르면 실제로 반영됩니다. (에러 행이 있어도 나머지 행은 처리됩니다)
                                    </p>
                                    <p className="text-xs text-slate-500 mt-1">
                                        수정 가능한 값은 보통 <span className="font-medium">이름/연락처</span>입니다. <span className="font-medium">`_id`, `이메일`, `_orgId`, `_role`</span> 같은 식별자 컬럼을 변경하면 업로드가 거부됩니다.
                                    </p>
                                </div>

                                {(importPreview?.errorCount ?? 0) > 0 && Array.isArray(importPreview?.errors) && (
                                    <div className="rounded border border-rose-200 bg-rose-50 px-4 py-3">
                                        <div className="font-medium text-rose-800 mb-2">에러 상세(상위 10건)</div>
                                        <ul className="space-y-1 text-xs text-rose-800">
                                            {importPreview.errors.slice(0, 10).map((e, idx) => (
                                                <li key={`${idx}-${String(e?.line || '')}`}>
                                                    - {e?.line ? `라인 ${e.line}: ` : ''}{e?.message || 'UNKNOWN'}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {(importPreview?.skippedNoChange ?? 0) > 0 && Array.isArray(importPreview?.skippedItems) && importPreview.skippedItems.length > 0 && (
                                    <div className="rounded border border-slate-200 bg-slate-50 px-4 py-3">
                                        <div className="font-medium text-slate-800 mb-2">변경 없음(샘플 10건)</div>
                                        <ul className="space-y-1 text-xs text-slate-700">
                                            {importPreview.skippedItems.slice(0, 10).map((it, idx) => (
                                                <li key={`${idx}-${String(it?.line || '')}`}>
                                                    - {it?.line ? `라인 ${it.line}: ` : ''}
                                                    {it?.email ? `${it.email} ` : ''}
                                                    {it?.beforePhone || it?.afterPhone ? `(${it.beforePhone || '-'} → ${it.afterPhone || '-'}) ` : ''}
                                                    {Array.isArray(it?.reasons) && it.reasons.length > 0 ? `[${it.reasons.join(', ')}]` : ''}
                                                </li>
                                            ))}
                                        </ul>
                                        <p className="text-[11px] text-slate-500 mt-2">
                                            PHONE_SAME는 업로드된 연락처(정규화 후)가 기존 값과 동일하다는 뜻입니다. (하이픈/공백 변경만 한 경우도 동일로 처리됩니다)
                                        </p>
                                    </div>
                                )}

                                <div className="flex justify-end gap-2 pt-2">
                                    <button
                                        type="button"
                                        className="rounded border px-3 py-2 text-sm"
                                        disabled={importApplying}
                                        onClick={() => {
                                            setImportPreviewOpen(false)
                                            setImportPayload(null)
                                            setImportPreview(null)
                                        }}
                                    >
                                        취소
                                    </button>
                                    <button
                                        type="button"
                                        className="rounded bg-brand-navy text-white px-3 py-2 text-sm disabled:opacity-50"
                                        disabled={importApplying}
                                        onClick={applyImport}
                                        title="실제 반영"
                                    >
                                        {importApplying ? '반영 중…' : '확인(반영)'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* 상세 모달 */}
            {showDetailModal && selectedCounterpart && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4" onClick={() => setShowDetailModal(false)}>
                    <div className="w-full max-w-2xl rounded bg-white p-6 max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold">{selectedCounterpart.name} 상세 정보</h3>
                            <button className="text-slate-500 hover:text-slate-700" onClick={() => setShowDetailModal(false)}>✕</button>
                        </div>

                        <div className="space-y-4 text-sm">
                            {/* 기본 정보 */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-slate-600 font-medium">이름</label>
                                    <div className="mt-1">
                                        {canEditGaProxyCounterpart && detailEditMode ? (
                                            <input
                                                className="w-full rounded border px-3 py-2"
                                                value={detailEditForm.name}
                                                onChange={(e) => setDetailEditForm(prev => ({ ...prev, name: e.target.value }))}
                                                disabled={detailSaving}
                                            />
                                        ) : (
                                            selectedCounterpart.name
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-slate-600 font-medium">직책</label>
                                    <div className="mt-1">{selectedCounterpart.position}</div>
                                </div>
                                <div>
                                    <label className="block text-slate-600 font-medium">소속</label>
                                    <div className="mt-1">{selectedCounterpart.orgName}</div>
                                </div>
                                <div>
                                    <label className="block text-slate-600 font-medium">담당 지사</label>
                                    <div className="mt-1">
                                        {canEditGaProxyCounterpart && detailEditMode ? (
                                            <div className="space-y-2">
                                                {/* 현재 담당 지사 목록 */}
                                                <div className="flex flex-wrap gap-2">
                                                    {(selectedGaBranches || []).length > 0 ? (
                                                        (selectedGaBranches || []).map((m) => (
                                                            <span
                                                                key={`${selectedCounterpart.id}-${String(m.gaBranchOrgId)}`}
                                                                className="inline-flex items-center gap-2 rounded border bg-slate-50 px-2 py-1 text-xs"
                                                            >
                                                                <span>{m.gaBranchOrgName || m.gaBranchOrgId}</span>
                                                                <button
                                                                    type="button"
                                                                    className="text-slate-500 hover:text-red-600 disabled:opacity-50"
                                                                    disabled={detailSaving}
                                                                    onClick={() => removeGaBranchMappingForSelected(m.gaBranchOrgId, m.gaBranchOrgName)}
                                                                    title="담당 지사 배정 해제"
                                                                >
                                                                    ✕
                                                                </button>
                                                            </span>
                                                        ))
                                                    ) : (
                                                        <span className="text-slate-500 text-xs">현재 배정된 담당 지사가 없습니다.</span>
                                                    )}
                                                </div>

                                                {/* 추가 */}
                                                <div className="flex gap-2">
                                                    <select
                                                        className="flex-1 rounded border px-3 py-2"
                                                        value={detailEditForm.gaBranchOrgId || ''}
                                                        onChange={(e) => setDetailEditForm(prev => ({ ...prev, gaBranchOrgId: e.target.value }))}
                                                        disabled={detailSaving}
                                                    >
                                                        <option value="">추가할 지사를 선택하세요</option>
                                                        {(gaBranchOptions || []).map(o => (
                                                            <option key={String(o.id)} value={String(o.id)}>
                                                                {o.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <button
                                                        type="button"
                                                        className="rounded border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
                                                        disabled={detailSaving || !detailEditForm.gaBranchOrgId}
                                                        onClick={addGaBranchMappingForSelected}
                                                    >
                                                        추가
                                                    </button>
                                                </div>
                                                <p className="text-xs text-slate-500">
                                                    대표 없이 복수 담당 지사를 관리합니다. (추가/해제는 즉시 반영)
                                                </p>
                                            </div>
                                        ) : (
                                            <span>
                                                {(() => {
                                                    // 상세 모달에서는 서버에서 /users/{id}/ga-branches로 로드한 목록이 가장 정확하다.
                                                    const fromMappings = (selectedGaBranches || [])
                                                        .map(x => (x?.gaBranchOrgName || '').toString().trim())
                                                        .filter(Boolean)
                                                    if (fromMappings.length > 0) return fromMappings.join(', ')
                                                    const names = Array.isArray(selectedCounterpart?.gaBranchOrgNames)
                                                        ? selectedCounterpart.gaBranchOrgNames.filter(Boolean)
                                                        : []
                                                    if (names.length > 0) return names.join(', ')
                                                    const fromList = selectedCounterpart?.gaBranchOrgName
                                                    return fromList || '-'
                                                })()}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-slate-600 font-medium">상태</label>
                                    <div className="mt-1">
                                        <span className={`px-2 py-1 rounded text-xs ${getStatusStyle(selectedCounterpart.status)}`}>{AvailabilityLabel[selectedCounterpart.status] || selectedCounterpart.status}</span>
                                    </div>
                                </div>
                            </div>

                            {/* 연락 정보 */}
                            <div>
                                <label className="block text-slate-600 font-medium">연락 정보</label>
                                <div className="mt-1 space-y-1">
                                    <div className="flex items-center gap-2">
                                        <Phone className="h-3.5 w-3.5 text-sgez-primary" aria-hidden="true" />
                                        {canEditGaProxyCounterpart && detailEditMode ? (
                                            <input
                                                className="flex-1 rounded border px-3 py-2"
                                                value={detailEditForm.phoneNumber}
                                                onChange={(e) => setDetailEditForm(prev => ({ ...prev, phoneNumber: e.target.value }))}
                                                disabled={detailSaving}
                                            />
                                        ) : (
                                            <span>{selectedCounterpart.phone || '—'}</span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Mail className="h-3.5 w-3.5 text-sgez-primary" aria-hidden="true" />
                                        <span>{selectedCounterpart.email || '—'}</span>
                                    </div>
                                </div>
                                {canEditGaProxyCounterpart && (
                                    <p className="text-xs text-slate-500 mt-2">
                                        이 담당자는 GA에서 대리 생성한 계정입니다. 수정/삭제는 여기서 진행하는 것을 권장합니다.
                                    </p>
                                )}
                            </div>

                            {/* KPI 정보 */}
                            <div>
                                <label className="block text-slate-600 font-medium">KPI (최근 90일)</label>
                                <div className="mt-1 grid grid-cols-2 gap-4">
                                    <div>
                                        <span className="text-slate-600">평균 응답시간:</span>
                                        <span className="ml-2 font-medium">{selectedCounterpart.avgResponseTime || '—'}시간</span>
                                    </div>
                                    <div>
                                        <span className="text-slate-600">평균 완료 리드타임:</span>
                                        <span className="ml-2 font-medium">{selectedCounterpart.avgLeadTime || '—'}일</span>
                                    </div>
                                    <div>
                                        <span className="text-slate-600">처리 건수:</span>
                                        <span className="ml-2 font-medium">{selectedCounterpart.completionCount || 0}건</span>
                                    </div>
                                    <div>
                                        <span className="text-slate-600">보완 요청률:</span>
                                        <span className="ml-2 font-medium">{selectedCounterpart.revisionRate || '—'}%</span>
                                    </div>
                                </div>
                            </div>

                            {/* GA 전용 메모 */}
                            <div>
                                <label className="block text-slate-600 font-medium">GA 전용 메모</label>
                                <div className="mt-1">
                                    <textarea
                                        className="w-full rounded border px-3 py-2"
                                        rows={3}
                                        placeholder="이 담당자에 대한 메모를 입력하세요"
                                        value={noteDraft}
                                        onChange={(e) => setNoteDraft(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 flex gap-2 justify-end">
                            {canEditGaProxyCounterpart && !detailEditMode && (
                                <>
                                    <button
                                        className="rounded border px-3 py-2 text-sm disabled:opacity-50"
                                        disabled={loadingSelectedGaBranches}
                                        onClick={() => {
                                            if (loadingSelectedGaBranches) {
                                                show('담당 지사 정보를 불러오는 중입니다. 잠시만 기다려주세요.', 'error')
                                                return
                                            }
                                            setDetailEditForm({
                                                name: selectedCounterpart.name || '',
                                                phoneNumber: selectedCounterpart.phone || '',
                                                // "추가"용 선택값은 기본 비움(사용자가 선택)
                                                gaBranchOrgId: '',
                                            })
                                            setDetailEditMode(true)
                                        }}
                                    >
                                        정보 수정
                                    </button>
                                    <button
                                        className="rounded border border-red-300 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                                        onClick={async () => {
                                            if (!selectedCounterpart) return
                                            const ok = window.confirm('이 담당자를 삭제(비활성화)하시겠습니까?\n\n삭제(비활성화) 후에는 목록에서 제외되며, 필요 시 다시 활성화할 수 있습니다.')
                                            if (!ok) return
                                            setDetailSaving(true)
                                            try {
                                                await api.patch(`/users/${selectedCounterpart.id}/deactivate`, { reason: 'GA 대리 생성 담당자 삭제' })
                                                show('삭제(비활성화) 완료', 'success')
                                                setShowDetailModal(false)
                                                setSelectedCounterpart(null)
                                                await reload()
                                            } catch (e) {
                                                show('삭제 실패', 'error')
                                            } finally {
                                                setDetailSaving(false)
                                            }
                                        }}
                                    >
                                        삭제
                                    </button>
                                </>
                            )}
                            {canEditGaProxyCounterpart && detailEditMode && (
                                <>
                                    <button
                                        className="rounded border px-3 py-2 text-sm"
                                        disabled={detailSaving}
                                        onClick={() => {
                                            setDetailEditMode(false)
                                            setDetailEditForm({ name: '', phoneNumber: '', gaBranchOrgId: '' })
                                        }}
                                    >
                                        취소
                                    </button>
                                    <button
                                        className="rounded bg-brand-navy text-white px-3 py-2 text-sm disabled:opacity-50"
                                        disabled={detailSaving}
                                        onClick={async () => {
                                            if (!selectedCounterpart) return
                                            const name = String(detailEditForm.name || '').trim()
                                            const phoneNumber = String(detailEditForm.phoneNumber || '').trim()
                                            if (!name) return show('이름을 입력해주세요', 'error')
                                            if (!phoneNumber) return show('전화번호를 입력해주세요', 'error')
                                            setDetailSaving(true)
                                            try {
                                                // 1) 이름/전화는 별도로 저장 (여기가 성공해도 아래 지사 변경이 실패할 수 있음)
                                                await api.patch(`/users/${selectedCounterpart.id}`, { name, phoneNumber })
                                                setSelectedCounterpart(prev => prev ? ({ ...prev, name, phone: phoneNumber }) : prev)

                                                show('수정 완료', 'success')
                                                setDetailEditMode(false)
                                                await reload()
                                            } catch (e) {
                                                show('이름/전화 수정에 실패했습니다', 'error')
                                            } finally {
                                                setDetailSaving(false)
                                            }
                                        }}
                                    >
                                        저장
                                    </button>
                                </>
                            )}
                            <button
                                className="rounded border px-3 py-2 text-sm"
                                onClick={async () => {
                                    if (!selectedCounterpart) return
                                    try {
                                        await saveNote(selectedCounterpart.id, noteDraft)
                                    } catch {
                                        // saveNote 내부에서 토스트 처리
                                    }
                                }}
                            >
                                메모 저장
                            </button>
                            <button className="rounded border px-3 py-2 text-sm" onClick={() => setShowDetailModal(false)}>닫기</button>
                            {/* 설계 의뢰는 설계관리 메뉴로 일원화(여기서는 숨김) */}
                        </div>
                    </div>
                </div>
            )}
        </section>
    )
}
